import { appConstants } from "@abp";
import { categoriesMap } from "@models";
import React, { useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * `index.html` i prerenderovane stranice nose kompletan SEO head zato što ga
 * crawleri koji ne izvršavaju JS jedino tako i vide; svaki takav tag je
 * obeležen sa `data-seo="static"`.
 *
 * Iste tagove u browseru postavlja i ova komponenta. Pod Reactom 19
 * `react-helmet-async` prelazi na nativni hoisting i više ne preuzima zatečene
 * tagove (što je ranije radio preko `data-rh`) — samo dodaje svoje pored
 * postojećih. Rezultat su dupli `title`, `description`, `og:*` i, najvidljivije,
 * dva kanonikala: početna je prerenderovana baš u `dist/index.html`, koji je
 * ujedno i SPA fallback, pa je njen kanonikal ka `/` završavao i na
 * `/requests/…` i na svakoj drugoj ruti koja nije prerenderovana.
 *
 * Statične tagove zato sklanjamo — ali tek pošto su Helmetovi već u dokumentu,
 * otud `useLayoutEffect` umesto brisanja na startu aplikacije: dok traje
 * inicijalni spinner, naslov u tabu ostaje onaj iz HTML-a.
 *
 * Posle prvog prolaza upit nema šta da nađe, pa nema potrebe da pamtimo da smo
 * ga već odradili.
 */
const useDropStaticSeoTags = () => {
  useLayoutEffect(() => {
    document
      .querySelectorAll('head [data-seo="static"]')
      .forEach((tag) => tag.remove());
  }, []);
};

interface IProps {
  title?: string;
  description?: string;
  keywords?: string;
  noIndex?: boolean;
  image?: string;
  imageAlt?: string;
  locale?: string;
  lang?: string;
}

export const SharedHelmet: React.FC<IProps> = ({
  title,
  description,
  keywords,
  noIndex = false,
  image,
  imageAlt,
  locale,
  lang,
}) => {
  useDropStaticSeoTags();

  const { pathname } = useLocation();
  const htmlLang = lang || "sr";
  const ogLocale = locale || "sr_RS";

  const mappedCategories = Object.values(categoriesMap).join(", ");

  const defaultTitle =
    title || "Rasporedi Me – Online zakazivanje termina";

  const defaultDescription =
    description ||
    `Zakažite termin online brzo i lako. Rasporedi Me povezuje vas sa najboljim frizerima, kozmetičarima, salonima za nokte i stomatolozima. Usluge: ${mappedCategories}.`;

  const defaultKeywords =
    keywords ||
    "zakazivanje termina online, frizer, kozmetičar, nokti, stomatolog, salon, rezervacija termina, Rasporedi Me";

  const cleanPathname = pathname.length > 1 && pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  const canonicalUrl = `${appConstants.appBaseUrl}${cleanPathname}`;

  const defaultImage = `${appConstants.appBaseUrl}/og-banner.png`;
  const ogImage = image || defaultImage;
  const ogImageAlt =
    imageAlt || "Rasporedi Me – platforma za online zakazivanje termina";

  return (
    <Helmet>
      <html lang={htmlLang} />

      <title>{defaultTitle}</title>
      <meta name="description" content={defaultDescription} />
      <meta name="keywords" content={defaultKeywords} />
      <meta name="author" content="Rasporedi Me" />
      {/*
        `follow` i kad je stranica van indeksa: prazna kategorija i dalje nosi
        linkove ka susednim kombinacijama grad × kategorija, a `nofollow` bi
        crawleru zatvorio put do njih.
      */}
      <meta
        name="robots"
        content={
          noIndex
            ? "noindex, follow"
            : "index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large"
        }
      />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={defaultTitle} />
      <meta property="og:description" content={defaultDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content={image ? "400" : "1200"} />
      <meta property="og:image:height" content={image ? "400" : "630"} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Rasporedi Me" />
      <meta property="og:locale" content={ogLocale} />


      {/* Twitter Card */}
      <meta name="twitter:card" content={image ? "summary" : "summary_large_image"} />
      <meta name="twitter:title" content={defaultTitle} />
      <meta name="twitter:description" content={defaultDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      <meta name="twitter:url" content={canonicalUrl} />
    </Helmet>
  );
};
